import React, { useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";

import {
  AUTOCOMPLETE_SOURCES,
  CONDITION_TYPES,
  CUSTOM_BASES,
  FIELD_TYPES,
  FILE_KINDS,
  MAX_FILE_MB,
  MAX_FILES,
  isValidCustomPath,
  LENGTH_TYPES,
  NO_DEFAULT_TYPES,
  NO_UNIQUE_TYPES,
  OPTION_TYPES,
  parseCustomSource,
  sectionKeyOf,
  sourceUrl,
  SUB_FIELD_TYPES,
} from "./custom-module-utils";
import { testSuggestionsApi } from "./custom-module-api";
import {
  addField,
  addSection,
  addSubfield,
  cloneField,
  moveField,
  nudgeField,
  nudgeSection,
  removeField,
  removeSection,
  removeSubfield,
  splitOptions,
  updateField,
  updateSection,
  updateSubfield,
} from "./builder-draft";
import { FieldInput, OptionBadge } from "./record-fields";
import { ReactComponent as DeleteRow } from "../../assets/svg/delete.svg";

const errorStyle = { color: "#ff4444" };

export const Label = ({ htmlFor, children }) => (
  <label htmlFor={htmlFor} className="fs-12 fw-500 text-grey mb-1">{children}</label>
);

// Server errors are keyed like "fields.2.label"; each card shows its own.
export const RowErrors = ({ errors, prefix }) =>
  Object.entries(errors)
    .filter(([key]) => key.startsWith(prefix))
    .map(([key, message]) => (
      <div key={key} className="fs-12" style={errorStyle}>{message}</div>
    ));

// "Show only when <field> is <value>", shared by fields and buttons. `fields`
// are the candidates that may control it (dropdown, multi-select, checkbox).
export const ConditionEditor = ({ id, fields, rule, onChange }) => {
  const controller = fields.find((field) => field.key === rule?.field);
  const firstValue = (field) => (field.type === "checkbox" ? true : splitOptions(field.optionsText)[0] ?? "");

  return (
    <div className="d-flex gap-2 flex-wrap">
      <select
        id={id}
        className="form-control"
        style={{ flex: "1 1 200px" }}
        value={rule?.field ?? ""}
        onChange={(e) => {
          const next = fields.find((field) => field.key === e.target.value);
          onChange(next ? { field: next.key, value: firstValue(next) } : null);
        }}
      >
        <option value="">Always</option>
        {fields.map((field) => (
          <option key={field.key} value={field.key}>Only when {field.label || field.key} is…</option>
        ))}
      </select>
      {controller && (
        <select
          className="form-control"
          style={{ flex: "1 1 140px" }}
          aria-label="Value"
          value={String(rule.value)}
          onChange={(e) => onChange({ ...rule, value: controller.type === "checkbox" ? e.target.value === "true" : e.target.value })}
        >
          {controller.type === "checkbox" ? (
            <>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </>
          ) : (
            splitOptions(controller.optionsText).map((option) => <option key={option} value={option}>{option}</option>)
          )}
        </select>
      )}
    </div>
  );
};

const CUSTOM = "__custom";
const PATH_HINT = "Letters, numbers, _ - / and an optional ?query, Ex: get_projects or get_state?country_id=1";

// Path box for a custom suggestion URL, with a Test button that calls it the
// way the dropdown will and reports what came back.
const CustomSourceEditor = ({ id, source, onChange }) => {
  const { base, path } = parseCustomSource(source);
  const [test, setTest] = useState(null);

  const runTest = async () => {
    const url = sourceUrl(source);
    if (!url) {
      setTest({ ok: false, message: `Not a valid path. ${PATH_HINT}.` });
      return;
    }
    setTest({ ok: true, message: "Testing..." });
    const response = await testSuggestionsApi(url);
    const list = response?.data?.suggestionlist;
    if (response?.status !== 200 || !response.data?.success || !Array.isArray(list)) {
      setTest({ ok: false, message: `Failed: ${response?.data?.message || "the URL did not return a suggestionlist"}.` });
    } else if (list.some((item) => item?.value === undefined || item?.label === undefined)) {
      setTest({ ok: false, message: "It answered, but each suggestion needs a value and a label." });
    } else {
      const sample = list.slice(0, 3).map((item) => item.label).join(", ");
      setTest({ ok: true, message: list.length ? `Works: ${list.length} suggestions, Ex: ${sample}` : "Works, but it returned no suggestions right now." });
    }
  };

  return (
    <>
      <div className="d-flex gap-2 flex-wrap">
        <select
          className="form-control"
          style={{ width: "auto" }}
          aria-label="API"
          value={base}
          onChange={(e) => {
            setTest(null);
            onChange(`${e.target.value}:${path}`);
          }}
        >
          {CUSTOM_BASES.map((item) => (
            <option key={item.value} value={item.value}>{item.label}</option>
          ))}
        </select>
        <input
          id={id}
          className="form-control"
          style={{ flex: "1 1 240px" }}
          value={path}
          placeholder="Ex: get_projects"
          onChange={(e) => {
            setTest(null);
            onChange(`${base}:${e.target.value.trim()}`);
          }}
        />
        <button type="button" className="fs-13 fw-600 cm-pill" disabled={!path} onClick={runTest}>
          Test
        </button>
      </div>
      {path && !isValidCustomPath(path) ? (
        <div className="fs-12" style={errorStyle}>{PATH_HINT}.</div>
      ) : (
        <div className="fs-12 fw-500 text-grey mt-1">
          The endpoint gets the typed text as <code>term</code> and must return{" "}
          <code>{"{ success: true, suggestionlist: [{ value, label }] }"}</code>, like the built-in lists.
        </div>
      )}
      {test && (
        <div className="fs-12 fw-500 mt-1" role="status" style={{ color: test.ok ? "#17B65B" : "#D61916" }}>
          {test.message}
        </div>
      )}
    </>
  );
};

// The columns of an "add more" row, Ex: Mobile number (Phone) + Type (Dropdown).
const SubfieldsEditor = ({ field, index, setDraft }) => {
  const id = (subIndex, name) => `field-${index}-sub-${subIndex}-${name}`;
  const update = (subIndex, patch) => setDraft((current) => updateSubfield(current, index, subIndex, patch));

  return (
    <>
      {field.subfields.map((sub, subIndex) => (
        <Row key={sub.uid} className="align-items-end mb-2 g-2">
          <Col md={4}>
            {subIndex === 0 && <Label htmlFor={id(subIndex, "label")}>COLUMN LABEL *</Label>}
            <input
              id={id(subIndex, "label")}
              className="form-control"
              aria-label={`Column ${subIndex + 1} label`}
              value={sub.label}
              placeholder="Ex: Mobile number"
              onChange={(e) => update(subIndex, { label: e.target.value })}
            />
          </Col>
          <Col md={3}>
            {subIndex === 0 && <Label htmlFor={id(subIndex, "type")}>TYPE</Label>}
            <select id={id(subIndex, "type")} className="form-control" aria-label={`Column ${subIndex + 1} type`} value={sub.type} onChange={(e) => update(subIndex, { type: e.target.value })}>
              {SUB_FIELD_TYPES.map((type) => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </Col>
          <Col md={3}>
            {subIndex === 0 && <Label htmlFor={id(subIndex, "options")}>OPTIONS (DROPDOWN)</Label>}
            <input
              id={id(subIndex, "options")}
              className="form-control"
              aria-label={`Column ${subIndex + 1} options`}
              disabled={sub.type !== "select"}
              value={sub.optionsText}
              placeholder={sub.type === "select" ? "Ex: Primary, Secondary" : ""}
              onChange={(e) => update(subIndex, { optionsText: e.target.value })}
            />
          </Col>
          <Col md={2} className="d-flex align-items-center gap-2" style={{ height: "38px" }}>
            <label className="fs-14 fw-500 text-grey d-flex align-items-center gap-1 mb-0">
              <input type="checkbox" checked={sub.required} onChange={(e) => update(subIndex, { required: e.target.checked })} />
              Required
            </label>
            {field.subfields.length > 1 && (
              <DeleteRow style={{ cursor: "pointer" }} role="button" aria-label={`Remove column ${subIndex + 1}`} onClick={() => setDraft((current) => removeSubfield(current, index, subIndex))} />
            )}
          </Col>
        </Row>
      ))}
      <div className="d-flex align-items-center gap-3 flex-wrap mt-2">
        {field.subfields.length < 6 && (
          <button type="button" className="fs-13 fw-600 cm-pill" onClick={() => setDraft((current) => addSubfield(current, index))}>
            + Add column
          </button>
        )}
        <label htmlFor={`field-${index}-max-rows`} className="fs-13 fw-500 text-grey mb-0">Max rows</label>
        <input
          id={`field-${index}-max-rows`}
          type="number"
          min={1}
          max={50}
          className="form-control"
          style={{ width: "100px" }}
          placeholder="No limit"
          value={field.max_rows}
          onChange={(e) => setDraft((current) => updateField(current, index, { max_rows: e.target.value }))}
        />
      </div>
    </>
  );
};

const FieldCard = ({ draft, setDraft, modules, errors, index, isDropTarget, drag }) => {
  const [open, setOpen] = useState(false);
  const field = draft.fields[index];
  const update = (patch) => setDraft((current) => updateField(current, index, patch));
  const id = (name) => `field-${index}-${name}`;
  const options = splitOptions(field.optionsText);
  const sections = draft.settings.sections;
  const controllers = draft.fields.filter((item, i) => i !== index && item.key && CONDITION_TYPES.includes(item.type));
  const tags = field.hidden
    ? ["Hidden"]
    : [
        field.required && "Required",
        field.unique && !NO_UNIQUE_TYPES.includes(field.type) && "Unique",
        field.visible_when && "Conditional",
        field.width === "full" && "Full width",
      ].filter(Boolean);

  const flag = (name, text, title) => (
    <label className="fs-14 fw-500 text-grey d-flex align-items-center gap-1 mb-0" title={title}>
      <input type="checkbox" checked={!!field[name]} onChange={(e) => update({ [name]: e.target.checked })} />
      {text}
    </label>
  );

  return (
    <div
      className={`p-3 mb-2 cm-field-card ${isDropTarget ? "cm-drop" : ""}`}
      onDragOver={drag.onDragOver}
      onDrop={drag.onDrop}
    >
      <div className="d-flex align-items-center justify-content-between gap-2 mb-2 flex-wrap">
        <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
          <span className="cm-handle fs-16" draggable onDragStart={drag.onDragStart} onDragEnd={drag.onDragEnd} title="Drag to move">
            ⋮⋮
          </span>
          <span className="fs-14 fw-600 text-dkblue text-truncate">{field.label.trim() || `Field ${index + 1}`}</span>
          {field.key && <span className="fs-12 fw-500 text-grey">{field.key}</span>}
          {tags.map((tag) => <span key={tag} className="cm-tag">{tag}</span>)}
        </div>
        <div className="d-flex align-items-center gap-1 flex-shrink-0">
          <button type="button" className="cm-icon-btn" aria-label="Move field up" onClick={() => setDraft((current) => nudgeField(current, index, -1))}>↑</button>
          <button type="button" className="cm-icon-btn" aria-label="Move field down" onClick={() => setDraft((current) => nudgeField(current, index, 1))}>↓</button>
          <button type="button" className="fs-13 fw-600 cm-pill" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
            {open ? "Fewer settings" : "More settings"}
          </button>
          <button type="button" className="fs-13 fw-600 cm-pill" onClick={() => setDraft((current) => cloneField(current, index))}>Clone</button>
          <DeleteRow style={{ cursor: "pointer" }} role="button" aria-label="Remove field" onClick={() => setDraft((current) => removeField(current, index))} />
        </div>
      </div>

      <Row>
        <Col md={4} className="mb-2">
          <Label htmlFor={id("label")}>LABEL *</Label>
          <input id={id("label")} className="form-control" value={field.label} onChange={(e) => update({ label: e.target.value })} placeholder="Ex: Vendor Name" />
        </Col>
        <Col md={4} className="mb-2">
          <Label htmlFor={id("type")}>TYPE</Label>
          <select id={id("type")} className="form-control" value={field.type} onChange={(e) => update({ type: e.target.value })}>
            {FIELD_TYPES.map((type) => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </Col>
        {OPTION_TYPES.includes(field.type) && (
          <Col md={4} className="mb-2">
            <Label htmlFor={id("options")}>OPTIONS *</Label>
            <input
              id={id("options")}
              className="form-control"
              value={field.optionsText}
              onChange={(e) => update({ optionsText: e.target.value })}
              placeholder="Comma separated, Ex: Open, Approved"
            />
          </Col>
        )}
        {field.type === "autocomplete" && (
          <Col md={4} className="mb-2">
            <Label htmlFor={id("source")}>SUGGESTIONS FROM *</Label>
            <select
              id={id("source")}
              className="form-control"
              value={parseCustomSource(field.source) ? CUSTOM : field.source}
              onChange={(e) => update({ source: e.target.value === CUSTOM ? "api:" : e.target.value })}
            >
              <option value="">Select</option>
              {AUTOCOMPLETE_SOURCES.map((source) => (
                <option key={source.value} value={source.value}>{source.label}</option>
              ))}
              {modules.map((module) => (
                <option key={module.slug} value={`module:${module.slug}`}>{module.name} (custom module)</option>
              ))}
              <option value={CUSTOM}>Custom URL…</option>
            </select>
          </Col>
        )}
        {field.type === "attachment" && (
          <>
            <Col md={4} className="mb-2">
              <Label htmlFor={id("file-kind")}>ALLOWED FILES</Label>
              <select id={id("file-kind")} className="form-control" value={field.file_kind || "any"} onChange={(e) => update({ file_kind: e.target.value })}>
                {Object.entries(FILE_KINDS).map(([value, kind]) => (
                  <option key={value} value={value}>{kind.label}</option>
                ))}
              </select>
            </Col>
            <Col md={4} className="mb-2">
              <Label htmlFor={id("max-files")}>MAX FILES</Label>
              <input
                id={id("max-files")}
                type="number"
                min={1}
                max={MAX_FILES}
                className="form-control"
                value={field.max_files ?? 1}
                onChange={(e) => update({ max_files: e.target.value })}
              />
            </Col>
            <Col md={4} className="mb-2">
              <Label htmlFor={id("max-size")}>MAX SIZE PER FILE (MB)</Label>
              <input
                id={id("max-size")}
                type="number"
                min={1}
                max={MAX_FILE_MB}
                className="form-control"
                value={field.max_size_mb ?? 5}
                onChange={(e) => update({ max_size_mb: e.target.value })}
              />
            </Col>
          </>
        )}
        {field.type === "repeater" && (
          <Col md={12} className="mb-2">
            <div className="fs-12 fw-500 text-grey mb-2">
              Each row of this field has these columns. The form shows one row and a + button to add more.
            </div>
            <SubfieldsEditor field={field} index={index} setDraft={setDraft} />
          </Col>
        )}
        {field.type === "autocomplete" && parseCustomSource(field.source) && (
          <Col md={12} className="mb-2">
            <Label htmlFor={id("source-path")}>SUGGESTION URL *</Label>
            <CustomSourceEditor id={id("source-path")} source={field.source} onChange={(source) => update({ source })} />
          </Col>
        )}
      </Row>

      <div className="d-flex align-items-center gap-4 flex-wrap">
        {!field.hidden && (
          <>
            {flag("required", "Required")}
            {!NO_UNIQUE_TYPES.includes(field.type) && flag("unique", "Unique", "Block saving when another record already has this value")}
            <label className="fs-14 fw-500 text-grey d-flex align-items-center gap-1 mb-0" title="Span every column of the section">
              <input type="checkbox" checked={field.width === "full"} onChange={(e) => update({ width: e.target.checked ? "full" : "normal" })} />
              Full width
            </label>
          </>
        )}
        {flag("hidden", "Hidden (table only)", "Stored on every record but never shown on the form, list or detail page")}
      </div>
      {field.hidden && (
        <div className="fs-12 fw-500 text-grey mt-2">
          Kept on each record but never shown on a page. New records get the default value (More settings); buttons can change it, and
          other fields and buttons can show or hide based on it. Ex: approve_status, is_confirmed.
        </div>
      )}

      {open && (
        <Row className="mt-3 pt-3" style={{ borderTop: "1px dashed #e5e5e5" }}>
          {!["repeater", "attachment"].includes(field.type) && !field.hidden && (
            <Col md={6} className="mb-2">
              <Label htmlFor={id("placeholder")}>PLACEHOLDER</Label>
              <input id={id("placeholder")} className="form-control" maxLength={150} value={field.placeholder} onChange={(e) => update({ placeholder: e.target.value })} />
            </Col>
          )}
          {!field.hidden && (
            <Col md={6} className="mb-2">
              <Label htmlFor={id("help")}>HELP TEXT</Label>
              <input
                id={id("help")}
                className="form-control"
                maxLength={300}
                value={field.help}
                placeholder="Shown under the input"
                onChange={(e) => update({ help: e.target.value })}
              />
            </Col>
          )}
          {!NO_DEFAULT_TYPES.includes(field.type) && (
            <Col md={6} className="mb-2">
              <Label htmlFor={id("default")}>DEFAULT VALUE</Label>
              <FieldInput
                id={id("default")}
                field={{ ...field, options, required: false, placeholder: "No default" }}
                value={field.default}
                onChange={(value) => update({ default: value })}
              />
            </Col>
          )}
          {field.type === "number" && (
            <>
              <Col md={3} className="mb-2">
                <Label htmlFor={id("min")}>MINIMUM</Label>
                <input id={id("min")} type="number" step="any" className="form-control" value={field.min} onChange={(e) => update({ min: e.target.value })} />
              </Col>
              <Col md={3} className="mb-2">
                <Label htmlFor={id("max")}>MAXIMUM</Label>
                <input id={id("max")} type="number" step="any" className="form-control" value={field.max} onChange={(e) => update({ max: e.target.value })} />
              </Col>
            </>
          )}
          {LENGTH_TYPES.includes(field.type) && (
            <Col md={3} className="mb-2">
              <Label htmlFor={id("max-length")}>MAX LENGTH</Label>
              <input
                id={id("max-length")}
                type="number"
                min={1}
                max={5000}
                className="form-control"
                value={field.max_length}
                placeholder={field.type === "textarea" ? "5000" : "255"}
                onChange={(e) => update({ max_length: e.target.value })}
              />
            </Col>
          )}
          {sections.length > 1 && (
            <Col md={6} className="mb-2">
              <Label htmlFor={id("section")}>SECTION</Label>
              <select
                id={id("section")}
                className="form-control"
                value={field.section}
                onChange={(e) => setDraft((current) => moveField(current, index, current.fields.length, e.target.value))}
              >
                {sections.map((section) => (
                  <option key={section.key} value={section.key}>{section.title || "Untitled section"}</option>
                ))}
              </select>
            </Col>
          )}
          {!field.hidden && (
            <Col md={6} className="mb-2">
              <Label htmlFor={id("condition")}>SHOW THIS FIELD</Label>
              <ConditionEditor id={id("condition")} fields={controllers} rule={field.visible_when} onChange={(rule) => update({ visible_when: rule })} />
              {!controllers.length && <div className="fs-12 fw-500 text-grey mt-1">Add a Dropdown or Checkbox field to make this field conditional.</div>}
            </Col>
          )}
          {OPTION_TYPES.includes(field.type) && options.length > 0 && (
            <Col md={12} className="mb-2">
              <Label>OPTION COLOURS</Label>
              <div className="d-flex flex-wrap gap-3">
                {options.map((option) => (
                  <label key={option} className="d-flex align-items-center gap-1 mb-0">
                    <input
                      type="color"
                      aria-label={`Colour for ${option}`}
                      value={field.option_colors?.[option] || "#546088"}
                      style={{ width: "28px", height: "28px", border: "none", padding: 0, background: "none" }}
                      onChange={(e) => update({ option_colors: { ...field.option_colors, [option]: e.target.value } })}
                    />
                    <OptionBadge field={field} option={option} />
                  </label>
                ))}
              </div>
            </Col>
          )}
        </Row>
      )}

      <RowErrors errors={errors} prefix={`fields.${index}.`} />
    </div>
  );
};

// Sections hold the fields; both the form and the detail page use this layout.
// Fields drag by their handle onto another card (lands before it) or into a
// section's empty space (lands last); the arrows do the same from the keyboard.
export const FieldsTab = ({ draft, setDraft, modules, errors }) => {
  const dragIndex = useRef(null);
  const [dropTarget, setDropTarget] = useState(null);
  const sections = draft.settings.sections;

  const allowDrop = (target) => (e) => {
    if (dragIndex.current === null) return;
    e.preventDefault();
    e.stopPropagation();
    setDropTarget(target);
  };

  const drop = (toIndex, section) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const from = dragIndex.current;
    dragIndex.current = null;
    setDropTarget(null);
    if (from !== null) setDraft((current) => moveField(current, from, toIndex, section));
  };

  return (
    <>
      {errors.fields && <div className="fs-12 mb-2" style={errorStyle}>{errors.fields}</div>}

      {sections.map((section, sectionIndex) => {
        const members = draft.fields
          .map((field, index) => ({ field, index }))
          .filter(({ field }) => sectionKeyOf(field, sections) === section.key);

        return (
          <div key={section.key} className="p-3 mb-3 cm-card" style={{ border: "1px solid #e5e7f0" }}>
            <div className="d-flex align-items-center gap-2 flex-wrap mb-3">
              <input
                className="form-control fw-600"
                style={{ flex: "1 1 220px" }}
                aria-label="Section title"
                placeholder="Section title (optional)"
                maxLength={100}
                value={section.title}
                onChange={(e) => setDraft((current) => updateSection(current, section.key, { title: e.target.value }))}
              />
              <select
                className="form-control"
                style={{ width: "auto" }}
                aria-label="Columns"
                value={section.columns}
                onChange={(e) => setDraft((current) => updateSection(current, section.key, { columns: Number(e.target.value) }))}
              >
                {[1, 2, 3].map((count) => (
                  <option key={count} value={count}>{count} column{count > 1 ? "s" : ""}</option>
                ))}
              </select>
              <button type="button" className="cm-icon-btn" aria-label="Move section up" disabled={sectionIndex === 0} onClick={() => setDraft((current) => nudgeSection(current, sectionIndex, -1))}>↑</button>
              <button
                type="button"
                className="cm-icon-btn"
                aria-label="Move section down"
                disabled={sectionIndex === sections.length - 1}
                onClick={() => setDraft((current) => nudgeSection(current, sectionIndex, 1))}
              >
                ↓
              </button>
              {sections.length > 1 && (
                <DeleteRow style={{ cursor: "pointer" }} role="button" aria-label="Remove section" onClick={() => setDraft((current) => removeSection(current, section.key))} />
              )}
            </div>

            <div
              className={dropTarget === `section-${section.key}` ? "cm-drop" : ""}
              style={{ minHeight: "56px", borderRadius: "12px" }}
              onDragOver={allowDrop(`section-${section.key}`)}
              onDrop={drop(draft.fields.length, section.key)}
            >
              {members.map(({ field, index }) => (
                <FieldCard
                  key={field.uid}
                  draft={draft}
                  setDraft={setDraft}
                  modules={modules}
                  errors={errors}
                  index={index}
                  isDropTarget={dropTarget === `field-${index}`}
                  drag={{
                    onDragStart: (e) => {
                      dragIndex.current = index;
                      e.dataTransfer.effectAllowed = "move";
                      e.dataTransfer.setData("text/plain", String(index));
                      const card = e.currentTarget.closest(".cm-field-card");
                      if (card) e.dataTransfer.setDragImage(card, 24, 24);
                    },
                    onDragEnd: () => {
                      dragIndex.current = null;
                      setDropTarget(null);
                    },
                    onDragOver: allowDrop(`field-${index}`),
                    onDrop: drop(index, section.key),
                  }}
                />
              ))}
              {!members.length && <div className="fs-13 fw-500 text-grey text-center p-3">Drag fields here, or add one.</div>}
            </div>

            <button type="button" className="fs-14 fw-600 cm-pill mt-2" onClick={() => setDraft((current) => addField(current, section.key))}>
              + Add field
            </button>
          </div>
        );
      })}

      <button type="button" className="fs-14 fw-600 cm-pill" onClick={() => setDraft(addSection)}>
        + Add section
      </button>
    </>
  );
};
